import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Typography, Box } from '@mui/material';
import { getPlayerColor } from '../../../utils/PlayerColorUtils';

const ScoreProgressChart = ({ data, playerColorMap = {} }) => {
  // State to track screen width for responsive adjustments
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1000);
  
  // Update window width on resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Determine if we're on a mobile device
  const isMobile = windowWidth <= 768;
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div style={{ width: '100%', height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>No data available</p>
      </div>
    );
  }

  // We'll use the shared color utility for consistent player colors
  
  const playerMap = new Map();
  
  // First identify all players
  data.forEach(entry => {
    if (!playerMap.has(entry.playerId)) {
      playerMap.set(entry.playerId, {
        id: entry.playerId,
        name: `${entry.firstName} ${entry.lastName}`,
        color: playerColorMap[entry.playerId] || getPlayerColor(entry.playerId),
      });
    }
  });
  
  // Get players array for rendering
  const players = Array.from(playerMap.values());
  
  // Since we need exactly 18 values for a standard Mario Kart Grand Prix,
  // we'll create an array with 18 slots and populate them based on race order
  
  // First, let's log a sample record to understand the data structure
  if (data.length > 0) {
    console.log('Sample record:', data[0]);
  }
  
  // Get all unique race index values (should be 1-16 for races)
  const allRaceIndices = new Set();
  data.forEach(entry => {
    if (entry.raceIndex !== undefined) {
      allRaceIndices.add(entry.raceIndex);
    }
  });
  console.log('Unique race indices found:', Array.from(allRaceIndices).sort((a, b) => a - b));
  
  // Create a mapping to ensure exactly 18 data points
  // We'll try using raceIndex if available, otherwise fall back to order in the array
  const raceData = [];
  const processedRaces = new Set();
  
  // Process all records and organize by race
  data.forEach(entry => {
    // Generate a unique identifier for this race
    // First try raceId if it exists
    const raceIdentifier = entry.raceId;
    
    if (!processedRaces.has(raceIdentifier)) {
      processedRaces.add(raceIdentifier);
      
      // Add a new race entry
      raceData.push({
        raceId: raceIdentifier,
        index: raceData.length, // Sequential index
        course: entry.course,
        courseId: entry.courseId,
        raceIndex: entry.raceIndex, // Store raceIndex if available
        players: {}
      });
    }
    
    // Find the race entry we just created or previously created
    const raceEntry = raceData.find(race => race.raceId === raceIdentifier);
    if (raceEntry) {
      // Add player data to this race
      raceEntry.players[entry.playerId] = {
        score: entry.cumulativeScore,
        name: `${entry.firstName} ${entry.lastName}`
      };
    }
  });
  
  console.log('Race data entries:', raceData.length);
  console.log('Race identifiers processed:', processedRaces.size);
  
  // Sort race data by raceIndex if available, otherwise keep original order
  raceData.sort((a, b) => {
    // If both have raceIndex, sort by that
    if (a.raceIndex !== undefined && b.raceIndex !== undefined) {
      return a.raceIndex - b.raceIndex;
    }
    // Otherwise maintain original order
    return a.index - b.index;
  });
  
  // If we still don't have exactly 18 races, log a warning
  if (raceData.length !== 18) {
    console.warn(`Expected 18 races but found ${raceData.length} races`);  
  }
  
  // Convert the processed race data to the format needed for the chart
  const raceSequence = raceData;
  
  // Create chart data structure based on the race sequence
  const chartData = raceSequence.map((race, index) => {
    // Create the base data point for this race
    const dataPoint = {
      name: `${index+1}: ${race.course}`,
      course: race.course,
      index: race.index
    };
    
    // Add each player's score to this data point
    Object.entries(race.players).forEach(([playerId, playerData]) => {
      dataPoint[`player_${playerId}`] = playerData.score;
      dataPoint[`player_${playerId}_name`] = playerData.name;
    });
    
    return dataPoint;
  });
  
  // No need for a separate mapping step since we're building the chart data directly
  
  // Fill in missing data for continuous lines
  // For each player, if they miss a race, carry forward their previous score
  players.forEach(player => {
    const playerId = player.id;
    let lastKnownScore = 0;
    
    for (let i = 0; i < chartData.length; i++) {
      const dataPoint = chartData[i];
      const scoreKey = `player_${playerId}`;
      
      if (dataPoint[scoreKey] !== undefined) {
        // Update the last known score
        lastKnownScore = dataPoint[scoreKey];
      } else {
        // Fill in the missing score with the last known value
        dataPoint[scoreKey] = lastKnownScore;
        dataPoint[`${scoreKey}_name`] = player.name;
      }
    }
  });

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Filter out undefined values and sort by value in descending order
      const sortedEntries = [...payload]
        .filter(entry => entry.value !== undefined && (entry.value || entry.value === 0))
        .sort((a, b) => b.value - a.value);
      
      return (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '8px', 
          border: '1px solid #ccc',
          borderRadius: '4px'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>{label}</p>
          <div style={{ marginTop: '5px' }}>
            {sortedEntries.map((entry, index) => {
              const playerId = entry.dataKey.split('_')[1];
              const playerName = chartData.find(d => d.name === label)?.[`player_${playerId}_name`] || '';
              
              return (
                <p key={index} style={{ 
                  margin: '3px 0',
                  color: entry.color,
                  fontWeight: index === 0 ? 'bold' : 'normal' // Bold the top score
                }}>
                  {index + 1}. {playerName}: {entry.value} points
                </p>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ width: '100%', height: '400px', position: 'relative' }}>
      <Box display="flex" flexDirection="column" alignItems="center" sx={{ marginTop: 0, marginBottom: 0 }}>
        <Typography variant="h6" sx={{ fontFamily: '"Press Start 2P", sans-serif', fontWeight: 'bold', fontSize: '1rem' }}>
          Score Progression
        </Typography>
      </Box>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" fill='black' fillOpacity={0.6}/>
          <XAxis 
            dataKey="name" 
            tick={props => {
              const { x, y, payload } = props;
              // Standard font size for axis values
              const fontSize = 11;
              
              // For all devices, show just the race number
              const displayText = payload.value.split(':')[0];
                
              return (
                <g transform={`translate(${x},${y})`}>
                  <text 
                    x={0} 
                    y={0} 
                    dy={10} 
                    textAnchor="middle" 
                    fill="#6B7280" 
                    fontSize={fontSize}
                  >
                    {displayText}
                  </text>
                </g>
              );
            }}
            height={isMobile ? 30 : 40}
            interval={0} // Show all ticks to ensure alignment
            tickMargin={5} // Reduced margin to bring labels closer
            label={{ value: 'Race Number', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fill: '#6B7280', fontSize: 14 } }}
            scale="point" // Use point scale for better alignment
            padding={{ left: 0, right: 0 }} // No extra padding on sides
            axisLine={true}
            tickLine={true}
          />
          <YAxis
            tick={{ fill: '#6B7280', fontSize: 11 }}
            width={35}
            label={{ value: 'Score', angle: -90, position: 'insideLeft', dx: -12, style: { textAnchor: 'middle', fill: '#6B7280', fontSize: 14 } }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            verticalAlign="top"
            height={26}
            wrapperStyle={{
              paddingBottom: isMobile ? 15 : 10,
              fontSize: 11,
              marginTop: -3
            }}
            iconSize={8} 
            layout="horizontal"
            align="center"
          />
          
          {players.map(player => (
            <Line
              key={player.id}
              type="monotone"
              dataKey={`player_${player.id}`}
              name={player.name}
              stroke={player.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 5 }}
              connectNulls={true} // Ensure lines connect even with null values
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ScoreProgressChart;
